import classNames from 'classnames';
import { useState } from 'react';
import type { DataAttributes } from 'shared/lib/data-auto-test';
import { Loader } from 'shared/ui/Loader';
import { MaterialIcon } from 'shared/ui/MaterialIcon';
import { formatNumberToView } from 'shared/lib/format';

import style from './ZipResultsItem.module.scss';

interface ZipResultsProps {
  type: 'valid' | 'duplicate' | 'invalid';
  resultType?: 'create' | 'campaign' | 'groups';
  className?: string;
  titleCounter?: number;
  onlyInvalid?: boolean;
  attributes?: DataAttributes['attributes'];
  body?: string;
  refetch?: () => Promise<unknown>;
  isFetching?: boolean;
}

export function ZipResultsItem(props: ZipResultsProps) {
  const {
    className,
    type,
    titleCounter,
    onlyInvalid,
    attributes,
    resultType = 'create',
    body,
    refetch,
    isFetching,
  } = props;
  const [isExpanded, setIsExpanded] = useState(false);

  if (!titleCounter) {
    return <></>;
  }

  const onClick = () => {
    if (isExpanded) {
      setIsExpanded(false);

      return;
    }

    if (refetch) {
      refetch().finally(() => setIsExpanded(true));

      return;
    }

    setIsExpanded(true);
  };

  const rootClass = classNames(
    'flex flex-col justify-start items-start rounded-md border w-full p-4 pr-[18px] cursor-pointer',
    className,
    { 'border-deep-green-200': type === 'valid' },
    { 'border-orange-100': type === 'duplicate' },
    { 'border-red-100': type === 'invalid' }
  );

  const getType = () => {
    if (type === 'valid') {
      return 'check_circle_outline';
    }

    if (type === 'duplicate') {
      return 'content_copy';
    }

    return 'error_outline';
  };

  const addPluralEnding = (amount: number) => (amount > 1 ? 's' : '');

  return (
    <div
      className={rootClass}
      onClick={onClick}
      {...attributes}
    >
      <div className="relative flex w-full items-start justify-start gap-4">
        <MaterialIcon
          className={classNames(
            { 'text-[20px]': resultType === 'create' },
            { 'text-deep-green-500': type === 'valid' },
            { 'text-orange-500': type === 'duplicate' },
            { [style.duplicateIcon]: type === 'duplicate' && resultType === 'create' },
            { 'text-red-500': type === 'invalid' },
            { 'text-[24px]': resultType !== 'create' },
            {
              'mt-2':
                (resultType !== 'create' && type === 'valid') || (type === 'duplicate' && resultType !== 'create'),
            }
          )}
        >
          {getType()}
        </MaterialIcon>
        <div className="flex-1">
          <div className="flex min-w-[229px] justify-between text-sm font-normal leading-5 tracking-normal">
            {type === 'valid' && resultType !== 'groups' && (
              <div className="font-medium">
                You have added <span className="font-bold">{formatNumberToView(titleCounter)}</span> valid zip code
                {addPluralEnding(titleCounter)}
              </div>
            )}
            {type === 'valid' && resultType === 'groups' && (
              <div className="font-medium">
                You have <span className="font-bold">{formatNumberToView(titleCounter)}</span> valid ZIP Code
                {addPluralEnding(titleCounter)} in <span className="pr-3">total</span>
              </div>
            )}
            {type === 'duplicate' && (
              <div className="font-medium">
                We have found and deleted <span className="font-bold">{formatNumberToView(titleCounter)}</span>{' '}
                duplicate{addPluralEnding(titleCounter)}
              </div>
            )}
            {type === 'invalid' && !onlyInvalid && (
              <div className="font-medium">
                We found <span className="font-bold">{formatNumberToView(titleCounter)}</span> invalid ZIP Code
                {addPluralEnding(titleCounter)}. You can go back and edit them or ignore and proceed (they will not be
                added to your {resultType === 'create' ? 'campaign' : 'group'}).
              </div>
            )}
            {type === 'invalid' && onlyInvalid && (
              <div className="font-medium">
                We have not found any valid ZIP Codes. Please check your data and try again or contact your Partnerships
                Manager for help.
              </div>
            )}
          </div>
        </div>
        <div className="h-6 w-6">
          {isFetching ? (
            <Loader className="absolute right-[-11px] top-[-3px] flex h-6 scale-50 justify-center" />
          ) : (
            <MaterialIcon
              className={classNames(
                'select-none text-slate-300',
                type !== 'invalid' && resultType !== 'create' && 'mt-2'
              )}
            >
              {isExpanded ? 'expand_less' : 'expand_more'}
            </MaterialIcon>
          )}
        </div>
      </div>
      {isExpanded && (
        <div
          className={classNames(
            style.zipList,
            'w-full mt-4 pl-10 font-medium text-sm text-slate-700 max-h-[256px] overflow-y-scroll break-words'
          )}
        >
          {body}
        </div>
      )}
    </div>
  );
}
